
import React from "react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { TerraceSubmission } from "@/types/terrace";
import { Eye, Check, X, Trash2, User } from "lucide-react";
import CoordinatesDisplay from "../terraces/CoordinatesDisplay";

interface SubmissionsTableProps {
  submissions: TerraceSubmission[];
  isLoading: boolean;
  handleViewSubmission: (submission: TerraceSubmission) => void;
  handleApproveFromTable: (submission: TerraceSubmission) => void;
  handleRejectFromTable: (submission: TerraceSubmission) => void;
  handleDeleteFromTable: (submission: TerraceSubmission) => void;
  statusFilter: 'all' | 'pending' | 'approved' | 'rejected';
}

const SubmissionsTable = ({
  submissions,
  isLoading,
  handleViewSubmission,
  handleApproveFromTable,
  handleRejectFromTable,
  handleDeleteFromTable,
  statusFilter
}: SubmissionsTableProps) => {
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-dusty-rose text-white">Pending</Badge>;
      case 'approved':
        return <Badge className="bg-teal-blue text-white">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-coral-pink text-white">Rejected</Badge>;
      default:
        return <Badge variant="outline" className="border-peach-orange/30">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-pulse text-lg">Loading submissions...</div>
      </div>
    );
  }

  if (submissions.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No submissions found for the selected filter.
      </div>
    );
  }

  return (
    <div className="overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead className="hidden md:table-cell">Type</TableHead>
            <TableHead className="hidden lg:table-cell">Address</TableHead>
            <TableHead className="hidden lg:table-cell">Coordinates</TableHead>
            <TableHead className="hidden xl:table-cell">Submitter</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {submissions.map((submission) => (
            <TableRow key={submission.id}>
              <TableCell className="font-medium">
                {submission.name}
                <div className="xl:hidden text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {submission.submitted_by || submission.submitter_name || 'Unknown'}
                </div>
                <div className="lg:hidden text-xs text-muted-foreground mt-1">
                  <CoordinatesDisplay lat={submission.lat} lng={submission.lng} />
                </div>
              </TableCell>
              <TableCell className="hidden md:table-cell capitalize">{submission.type}</TableCell>
              <TableCell className="hidden lg:table-cell">{submission.address}</TableCell>
              <TableCell className="hidden lg:table-cell">
                <CoordinatesDisplay lat={submission.lat} lng={submission.lng} />
              </TableCell>
              <TableCell className="hidden xl:table-cell">
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <User className="h-3 w-3" />
                  {submission.submitted_by || submission.submitter_name || 'Unknown'}
                </div>
              </TableCell>
              <TableCell>{getStatusBadge(submission.status)}</TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1 sm:gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 w-8 p-0 border-peach-orange/30"
                    onClick={() => handleViewSubmission(submission)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  
                  {statusFilter === 'pending' && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 w-8 p-0 border-peach-orange/30"
                        onClick={() => handleApproveFromTable(submission)}
                      >
                        <Check className="h-4 w-4 text-teal-blue" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 w-8 p-0 border-peach-orange/30"
                        onClick={() => handleRejectFromTable(submission)}
                      >
                        <X className="h-4 w-4 text-coral-pink" />
                      </Button>
                    </>
                  )}
                  
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 w-8 p-0 border-peach-orange/30"
                    onClick={() => handleDeleteFromTable(submission)}
                  >
                    <Trash2 className="h-4 w-4 text-coral-pink" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default SubmissionsTable;
